// Display formatters. Money values are NUMERIC(20,2) sent as strings.

// 金額前面加 $，每三位數字加逗號 (per 2026-05-08 user request)。
// 注意：這個 $ 是視覺記號 — 真正的幣別 (TWD/USD/...) 還是會用其他欄位顯示。
// 目前 zh-TW Intl.NumberFormat 不會自動加 currency symbol，所以手動 prefix。
//
// 2026-05-18: 金額一律 round 到整數 — pro_rata 等 derive 出來的數字會帶 cents
// (e.g. $461,538.46), 顯示分位看起來不像金融報表。 有 cents 精度需求者 (e.g.
// price_per_share) 應改用 formatPrice / formatShares。
export function formatMoney(value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === '') return '—';
  const n = typeof value === 'string' ? Number(value) : value;
  if (!Number.isFinite(n)) return String(value);
  const formatted = new Intl.NumberFormat('zh-TW', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(n);
  // 負號前置：例 -123 → "-$123"；正數 → "$123"
  return n < 0 ? `-$${formatted.replace(/^-/, '')}` : `$${formatted}`;
}

// 股價專用 — 4 位小數 (V056 投資輪次 price_per_share)
export function formatPrice(value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === '') return '—';
  const n = typeof value === 'string' ? Number(value) : value;
  if (!Number.isFinite(n)) return String(value);
  const formatted = new Intl.NumberFormat('zh-TW', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 4,
  }).format(n);
  return n < 0 ? `-$${formatted.replace(/^-/, '')}` : `$${formatted}`;
}

// 萬 (10,000) 為單位顯示。儀表板 KPI 卡用。
// 1,450,000,000 → "145,000 萬"
// 12,345,678   → "1,235 萬"（四捨五入到萬位）
export function formatMoneyWan(value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === '') return '—';
  const n = typeof value === 'string' ? Number(value) : value;
  if (!Number.isFinite(n)) return String(value);
  const wan = Math.round(n / 10_000);
  return `${new Intl.NumberFormat('zh-TW').format(wan)} 萬`;
}

// 百萬 (Mn, 1,000,000) 為單位顯示。儀表板 KPI 卡用。
// 1,450,000,000 → "$1,450 Mn"
// 12,345,678    → "$12 Mn"（四捨五入到百萬位）
// 跟 formatMoney 一樣前綴 $，因為 dashboard 已經有 hover tooltip 顯示完整數字。
// 第二參數 currencyPrefix: 'NT$' / '$' / '€' ... 預設 '$'.
export function formatMoneyMillion(
  value: string | number | null | undefined,
  currencyPrefix = '$',
): string {
  if (value === null || value === undefined || value === '') return '—';
  const n = typeof value === 'string' ? Number(value) : value;
  if (!Number.isFinite(n)) return String(value);
  const millions = Math.round(n / 1_000_000);
  const formatted = new Intl.NumberFormat('zh-TW').format(millions);
  return n < 0
    ? `-${currencyPrefix}${formatted.replace(/^-/, '')} Mn`
    : `${currencyPrefix}${formatted} Mn`;
}

/**
 * KPI 卡片專用的縮寫金額（2026-08-03 使用者：「卡片如果數字太大 建議都用 Mn 來算」）。
 *
 * ⚠ **只用在 KPI 卡與圖表軸** —— 明細表格 / 金額輸入 / Invoice / 對帳單一律維持完整
 *   數字。 原因不是美觀：190,500,000 與 190,543,210 都會顯示成 190.5 Mn，對帳時
 *   看縮寫會對錯帳。 概覽可以糊，憑證不行。
 *
 * 級距（沿用既有的 Mn 詞彙，不混用萬/億）：
 *   < 1 Mn      → 完整數字（縮寫反而更難讀：$0.85 Mn vs $850,000）
 *   1 Mn ~ 1 Bn → $190.5 Mn（小數 1 位，整數就不加 .0）
 *   >= 1 Bn     → $1.45 Bn（小數 2 位，尾端 0 去掉）
 *
 * 2026-09-14 使用者: 台幣不寫 NT —— 預設前綴從 `NT$` 改成 `$`, 跟 formatMoney /
 * 儀表板同一張臉 (原本首頁 `$861 Mn`、點進基金詳情頁卻是 `NT$190.5 Mn`)。
 */
export function formatMoneyCompact(
  value: string | number | null | undefined,
  currencyPrefix = '$',
): string {
  if (value === null || value === undefined || value === '') return '—';
  const n = typeof value === 'string' ? Number(value) : value;
  if (!Number.isFinite(n)) return String(value);
  const abs = Math.abs(n);
  const sign = n < 0 ? '-' : '';
  const nf = (v: number, d: number) =>
    new Intl.NumberFormat('zh-TW', { minimumFractionDigits: 0, maximumFractionDigits: d }).format(
      v,
    );
  if (abs < 1_000_000) return `${sign}${currencyPrefix}${nf(abs, 0)}`;
  if (abs < 1_000_000_000) return `${sign}${currencyPrefix}${nf(abs / 1_000_000, 1)} Mn`;
  return `${sign}${currencyPrefix}${nf(abs / 1_000_000_000, 2)} Bn`;
}

/**
 * TWD 換算後的金額 (KPI 卡 hover 的完整數字、合計列)。
 *
 * 2026-09-14 使用者: 台幣不寫 NT —— 原本前綴 `NT$`, 改成跟 formatMoney 一模一樣 (`$`)。
 * 留著這個名字是因為呼叫端用它標明「這個數字已經換算成台幣」, 不是因為長相不同。
 */
export function formatMoneyTwd(value: string | number | null | undefined): string {
  return formatMoney(value);
}

export function formatInt(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—';
  return new Intl.NumberFormat('zh-TW').format(value);
}

export function formatDate(value: string | null | undefined): string {
  if (!value) return '—';
  return value.slice(0, 10);
}

// 日期+時間: 'YYYY-MM-DD HH:mm' (統一取代散落的 toLocaleString() —— 那會依瀏覽器
// locale 出 '2026/7/16 下午2:00' 之類, 跟全站 YYYY-MM-DD 混用; UX 稽核 #9)
export function formatDateTime(value: string | null | undefined): string {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value.replace('T', ' ').slice(0, 16);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
}

/**
 * 時間戳 → 日期 (YYYY-MM-DD), 用**看的人所在時區**換算。
 *
 * ⚠ 不要用 `iso.slice(0, 10)` —— 那是 **UTC** 的日子。 台北 0~8 點發生的事會被寫成
 *   前一天: 正式站 48 張簽核單裡有 14 張 (29%) 會錯, 例如 2026-09-06T23:03Z 其實是
 *   台北 9/7 早上七點, 畫面卻寫 9/6。
 * ⚠ 這支是給「後端沒有先算好台北日期」的顯示點用的。 清單欄位優先讓後端回一個
 *   算好的 `*_on` (例如 approvals 的 applied_on / paid_on) —— 那才不受看的人所在
 *   時區影響 (有人在國外開就會看到不一樣的日子)。
 */
export function formatTsDate(value: string | null | undefined): string {
  if (!value) return '—';
  return formatDateTime(value).slice(0, 10);
}

// 股數: 千分位, 最多 4 位小數 (跟 V063 shares NUMERIC 對齊)
// 取代各 page 散落的 toLocaleString('en-US', { maximumFractionDigits: 4 }) (S8)
export function formatShares(value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === '') return '—';
  const n = typeof value === 'string' ? Number(value) : value;
  if (!Number.isFinite(n)) return String(value);
  return n.toLocaleString('en-US', { maximumFractionDigits: 4 });
}

// 百分比: 0.123 → "12.3%" / 0.05 → "5.0%"
// (取代散落的 (n * 100).toFixed(...) + '%' inline)
export function formatPercent(
  value: number | string | null | undefined,
  fractionDigits = 1,
): string {
  if (value === null || value === undefined || value === '') return '—';
  const n = typeof value === 'string' ? Number(value) : value;
  if (!Number.isFinite(n)) return '—';
  return `${(n * 100).toFixed(fractionDigits)}%`;
}

/**
 * 金額 + 幣別的 canonical 寫法 — 全站唯一 ($45,000; 非 TWD 補後綴 "$45,000 USD")。
 * UX 稽核 #5 (2026-07-17): 同一張簽核單在 列表 $45,000 / 詳情 TWD 45,000 /
 * 薪資 NT$ 45,000 三種長相 — 金融內部工具同一筆錢兩種寫法最傷信任感。
 */
export function formatMoneyCcy(
  value: string | number | null | undefined,
  currency?: string | null,
): string {
  if (value == null || value === '') return '—';
  const base = formatMoney(value);
  return currency && currency !== 'TWD' ? `${base} ${currency}` : base;
}

/**
 * 今天 (台北)，YYYY-MM-DD。
 *
 * 2026-08-08: 全站原本有 13 份 `new Date().toISOString().slice(0, 10)` —— 那是
 * **UTC** 的今天, 台北 00:00-08:00 會拿到昨天。 表單預設日期就少一天, 而踩到的
 * 欄位全是要記進帳的: 到款日 / 分錄日 / 估值日 / KYC 驗證日 / 請假起訖。
 * 系統的時間基準已統一為台北 (DB TimeZone + cron.timezone + 後端 lib/dates.ts),
 * 前端這份是同一條線的最後一塊。
 */
export function todayIso(): string {
  // en-CA 的格式就是 YYYY-MM-DD。
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Taipei' }).format(new Date());
}

/**
 * 檔案大小 — B / KB / MB。
 *
 * 2026-08-08: 原本 fmtSize() 在四個上傳元件各寫一份, 而且**簽名不一致** —
 * PendingAttachments 吃 number (File.size), SignoffAttachments / EmployeeDocuments
 * 吃 string | null (API 回的 size_bytes), DocumentsPanel 則是 inline 算 MB。
 * 同一個數字四種寫法 → 同一個檔案在不同畫面會顯示成不同字串。
 */
export function formatBytes(value: number | string | null | undefined): string {
  if (value == null || value === '') return '—';
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n)) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}
